<script setup>
import {computed} from 'vue'
import {useOddsmatcherStore} from "@/stores/OddsmatcherStore";

const oddsmatcherStore = useOddsmatcherStore()
// Define props
const props = defineProps({
  bet: Object,
  freebet: Boolean
})

// Computed property to format the date and time separately
const formattedDate = computed(() => {
  const date = new Date(props.bet.eventStart);
  return date.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
});

const formattedTime = computed(() => {
  const date = new Date(props.bet.eventStart);
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  });
});

// Determine the sport icon (football or horse)
const sportIcon = computed(() => {
  return props.bet.sport === 'football' ? 'football-icon-class' : 'horse-icon-class';
});
</script>

<template>
  <div class="bet-info">
    <!-- Row 1: Date, Time, Sport, and Event Description -->
    <div class="bet-info__row event-info">
      <div class="event-info__left event-info__date">
        <span><i class="bi bi-calendar"></i> {{ formattedDate }}</span>
        <span><i class="bi bi-clock"></i> {{ formattedTime }}</span>
      </div>
      <div class="event-info__right">
        <i :class="sportIcon" class="event-info__sport"></i>
        <span class="event-info__description">{{ bet.eventDescription }}</span>
      </div>
    </div>

    <!-- Row 2: Outcome (Centered horizontally) -->
    <div class="bet-info__row outcome-row bet-info__selection">
      <span class="lg-wrap">{{ bet.selection }}</span>
    </div>

    <!-- Row 3: Back and Lay Bets on the same row -->
    <div class="bet-info__row bet-info__row--split">
      <div class="bet-info__col">
        <span class="bet-info__label--small sm-only">Back Bet</span>
        <div class="bet-info__bet">
          <div class="bet-info__box back-bookie">{{ bet.backBet.bookieDisplay }}</div>
          <div class="bet-info__box back-odds">{{
              parseFloat(parseFloat(bet.backBet.odds).toFixed(4)).toString()
            }}
          </div>
        </div>
      </div>
      <div class="bet-info__col">
        <span class="bet-info__label--small sm-only">Lay Bet</span>
        <div class="bet-info__bet">
          <div class="bet-info__box lay-bookie">{{ bet.layBet.bookieDisplay }}</div>
          <div class="bet-info__box lay-odds">{{ parseFloat(parseFloat(bet.layBet.odds).toFixed(4)).toString() }}</div>
        </div>
      </div>
    </div>

    <!-- Row 4: Profit and Open Calculator -->
    <div class="bet-info__row bet-info__row--split">
      <div class="bet-info__col rating-col">
        <span v-if="freebet">{{ bet.snrRating.toFixed(2) }}%</span>
        <span v-else>{{ bet.srRating.toFixed(2) }}%</span>

      </div>
      <div class="bet-info__col">
        <button class="calc-button" @click="oddsmatcherStore.handleSelectMatchedBet(bet)">
          <i class="bi bi-calculator"></i> <span class="sm-only">Open Calc</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* General styles for small screens (card layout) */
.bet-info {
  padding: 20px;
  border: 1px solid lightgrey;
  border-radius: 10px;
  background-color: #f9f9f9;
  margin: 20px auto;
}

.bet-info__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid lightgrey;
}

.bet-info__row:last-child {
  border-bottom: none;
}

.event-info {
  display: flex;
  justify-content: space-between;
}

.event-info__left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.event-info__left span {
  display: flex;
  align-items: center;
}

.event-info__left i {
  margin-right: 5px;
}

.event-info__right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.event-info__right h6 {
  margin: 0;
  font-weight: bold;
}

.outcome-row {
  text-align: center;
  padding: 20px 0;
}

/* Back and Lay Bet Row */
.bet-info__row--split {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.bet-info__col {
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;
}

.bet-info__bet {
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
}

.bet-info__label--small {
  font-size: 0.8em;
  font-weight: bold;
  color: lightgrey;
  margin-bottom: 10px;
}

/* Styling the Boxes for Back and Lay Bets */
.bet-info__box {
  padding: 10px;
  border-radius: 10px;
  text-align: center;
  flex: 1;
  margin: 0 1px;
}

.back-bookie, .lay-bookie {
  background-color: lightgrey;
}

.back-odds {
  background-color: #00b073;
  color: white;
}

.lay-odds {
  background-color: #005ea6;
  color: white;
}

.rating-col {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.calc-button {
  background-color: var(--clr-primary-100);
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 5px;
  cursor: pointer;
}

.calc-button:hover {
  background-color: var(--clr-primary-40);
}


/* Media query for large screens (table layout) */
@media (min-width: 992px) {
  .bet-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    margin: 0;
    border-radius: 0;
    border-bottom: 1px solid lightgrey;
  }

  .sm-only {
    display: none
  }

  .bet-info__row {
    flex-direction: row;
    border-bottom: none;
    padding: 0;
  }

  .event-info__right {
    flex-direction: row;
    align-items: center;
    gap: 1rem;
  }

  .event-info__left span, .event-info__right h6 {
    margin-right: 20px;
  }

  .bet-info__row--split {
    flex-direction: row;
    align-items: center;
    justify-content: space-around;
  }

  .bet-info__bet {
    flex-direction: row;
    width: 150px;
  }

  .rating-col {
    flex-direction: row;
    align-items: center;
    text-align: center;
    width: 72px;
    margin-left: 8px;
  }

  .event-info__date {
    width: 190px;
  }

  .event-info__description {
    width: 150px;
  }

  .bet-info__selection {
    width: 130px;
    text-align: left;
  }

  .lg-wrap {
    display: inline-block;
    max-width: 120px; /* Limit the width to 120px */
    white-space: normal; /* Allow text to wrap within this width */
    word-wrap: break-word; /* Ensure long words break and wrap */
    margin-right: 10px;
  }


  .back-odds, .lay-odds {
    max-width: 75px;
  }

  .calc-button {
    background-color: inherit;
    color: var(--clr-primary-100);
    font-size: 1.75rem;

  }

}

@media (min-width: 1092px) {
  .bet-info__bet {
    width: 200px;
  }
}


/* Define the icon classes */
.football-icon-class {
  background-image: url('@/assets/football-icon.svg');
  width: 24px;
  height: 24px;
  display: inline-block;
  background-size: cover;
}

.horse-icon-class {
  background-image: url('@/assets/horse-icon.svg');
  width: 24px;
  height: 24px;
  display: inline-block;
  background-size: cover;
}
</style>
